import { isRecord, isTimestamp, type DateValue } from "../types/data";
import type { FilmMetadata, Season } from "../types/media";
import type { Rating, SeasonRating, ViewingEntry } from "../types/rating";
import type { SeasonIdentity } from "./episodeRatings";
export type WatchSeason = Season | SeasonRating | SeasonIdentity;

export interface WatchSummary extends FilmMetadata {
  version?: number; userId?: string; totalCount?: number; rewatchCount?: number; knownDatesCount?: number;
  firstKnownAt?: DateValue; lastWatchedAt?: DateValue; createdAt?: DateValue; updatedAt?: DateValue;
  virtual?: boolean; seasonKey?: string; seasonNumber?: number | null; seasonName?: string;
}
export interface WatchSummaryOptions {
  film?: FilmMetadata; rating?: Rating; previous?: WatchSummary; entries?: ViewingEntry[]; updatedAt?: DateValue;
}
export interface SeasonWatchSummaryOptions extends WatchSummaryOptions { season?: WatchSeason; seasonRating?: SeasonRating; }
export type WatchFilter = "all" | "once" | "rewatched" | "three_plus" | "recent_rewatch";
export type WatchSort = "rating_date" | "view_count" | "last_view" | "first_view";

export const WATCH_HISTORY_VERSION = 1;
export const MAX_VIEWINGS_PER_TITLE = 400;

export const toWatchDate = (value: DateValue | undefined): Date | null => {
  if (!value) return null;
  if (isTimestamp(value)) return value.toDate();
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (isRecord(value)) {
    const seconds = Number(value.seconds ?? value._seconds);
    const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds ?? 0);
    if (Number.isFinite(seconds)) {
      const parsed = new Date((seconds * 1000) + Math.floor(nanoseconds / 1000000));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  if (typeof value === "string") {
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      const [, year, month, day] = dateOnly;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day), 12);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  const parsed = new Date(typeof value === "object" ? String(value) : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getWatchFilmId = (value: FilmMetadata = {}) =>
  String(value.filmId || value.movieId || value.tmdbId || value.id || "");

export const getWatchTitle = (film: FilmMetadata = {}, rating: Rating = {}) =>
  film.title || film.name || rating.title || rating.filmTitle || rating.name || "Неизвестный фильм";

export const getWatchMediaType = (film: FilmMetadata = {}, rating: Rating = {}) => {
  const value = String(
    film.mediaType || film.media_type || rating.mediaType || rating.media_type || ""
  ).toLowerCase();
  if (value === "tv" || value === "series" || film.first_air_date || rating.first_air_date) {
    return "tv";
  }
  return "movie";
};

export const getWatchSeasonKey = (season: WatchSeason = {}) => {
  const value = season.id ?? season.seasonId ?? season.season_number ?? season.seasonNumber;
  return value == null || value === "" ? "" : String(value);
};

export const getWatchSeasonNumber = (season: WatchSeason = {}) => {
  const value = Number(season.season_number ?? season.seasonNumber);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

export const createVirtualFirstViewing = (rating: Rating = {}, film: FilmMetadata = {}) => {
  const filmId = getWatchFilmId(rating) || getWatchFilmId(film);
  if (!filmId) return null;
  const watchedAt = toWatchDate(
    rating.firstRatedAt || rating.ratedAt || rating.firstTrackedAt || rating.updatedAt
  );
  return {
    id: "existing-first-viewing",
    filmId,
    ordinal: 1,
    watchedAt,
    dateUnknown: !watchedAt,
    source: rating.importSource ? "import" : "existing_data",
    createdAt: toWatchDate(rating.firstTrackedAt || rating.updatedAt) || watchedAt,
    virtual: true
  };
};

export const createVirtualSeasonFirstViewing = (seasonRating: SeasonRating = {}, film: FilmMetadata = {}, season: WatchSeason = {}) => {
  const filmId = getWatchFilmId(film);
  const seasonKey = getWatchSeasonKey(seasonRating) || getWatchSeasonKey(season);
  if (!filmId || !seasonKey || !Number.isFinite(Number(seasonRating?.userRate))) return null;
  const watchedAt = toWatchDate(
    seasonRating.firstRatedAt || seasonRating.ratedAt || seasonRating.updatedAt
  );
  return {
    id: "existing-first-viewing",
    filmId,
    seasonKey,
    ordinal: 1,
    watchedAt,
    dateUnknown: !watchedAt,
    source: "existing_data",
    targetType: "season",
    seasonNumber: getWatchSeasonNumber(seasonRating) ?? getWatchSeasonNumber(season),
    createdAt: watchedAt,
    virtual: true
  };
};

const dateMillis = (value: DateValue | undefined) => toWatchDate(value)?.getTime() || 0;

export const sortViewingEntriesAscending = <T extends ViewingEntry>(entries: readonly T[] = []): T[] =>
  [...entries].sort((first, second) => {
    const firstDate = dateMillis(first.watchedAt);
    const secondDate = dateMillis(second.watchedAt);
    if (!firstDate && secondDate) return -1;
    if (firstDate && !secondDate) return 1;
    if (firstDate !== secondDate) return firstDate - secondDate;
    const firstCreated = dateMillis(first.createdAt);
    const secondCreated = dateMillis(second.createdAt);
    if (firstCreated !== secondCreated) return firstCreated - secondCreated;
    return String(first.id || "").localeCompare(String(second.id || ""));
  });

export const normalizeViewingOrdinals = <T extends ViewingEntry>(entries: readonly T[] = []) =>
  sortViewingEntriesAscending(entries).map((entry, index) => ({
    ...entry,
    ordinal: index + 1
  }));

export const sortViewingEntriesForDisplay = <T extends ViewingEntry>(entries: readonly T[] = []) =>
  [...normalizeViewingOrdinals(entries)].sort((first, second) => {
    const firstDate = dateMillis(first.watchedAt);
    const secondDate = dateMillis(second.watchedAt);
    if (firstDate && secondDate) return secondDate - firstDate;
    if (firstDate) return -1;
    if (secondDate) return 1;
    return Number(second.ordinal || 0) - Number(first.ordinal || 0);
  });

const compactObject = <T extends Record<string, unknown>>(value: T): T => Object.fromEntries(
  Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)
) as T;

export const buildWatchSummary = ({
  film = {},
  rating = {},
  previous = {},
  entries = [],
  updatedAt = new Date()
}: WatchSummaryOptions = {}) => {
  const normalizedEntries = normalizeViewingOrdinals(entries);
  const knownDates = normalizedEntries
    .map((entry) => toWatchDate(entry.watchedAt))
    .filter((date): date is Date => Boolean(date))
    .sort((first, second) => first.getTime() - second.getTime());
  const filmId = getWatchFilmId(film) || getWatchFilmId(rating) || String(previous.filmId || "");
  const posterPath =
    film.poster_path || film.posterPath || rating.posterPath || rating.filmPoster || previous.posterPath || "";
  const releaseDate =
    film.release_date || film.first_air_date || rating.releaseDate || rating.filmReleaseDate || previous.releaseDate || "";

  return compactObject({
    version: WATCH_HISTORY_VERSION,
    userId: previous.userId,
    filmId,
    title:
      film.title || film.name || rating.title || rating.filmTitle || rating.name ||
      previous.title || "Неизвестный фильм",
    posterPath,
    releaseDate,
    mediaType: previous.mediaType || getWatchMediaType(film, rating),
    totalCount: normalizedEntries.length,
    rewatchCount: Math.max(0, normalizedEntries.length - 1),
    knownDatesCount: knownDates.length,
    firstKnownAt: knownDates[0] || null,
    lastWatchedAt: knownDates.at(-1) || null,
    createdAt: previous.createdAt || updatedAt,
    updatedAt
  });
};

export const buildVirtualWatchSummary = (rating: Rating = {}, film: FilmMetadata = {}) => {
  const firstEntry = createVirtualFirstViewing(rating, film);
  if (!firstEntry) return null;
  return {
    ...buildWatchSummary({ film, rating, entries: [firstEntry] }),
    virtual: true
  };
};

export const buildSeasonWatchSummary = ({
  film = {},
  season = {},
  seasonRating = {},
  previous = {},
  entries = [],
  updatedAt = new Date()
}: SeasonWatchSummaryOptions = {}) => {
  const base = buildWatchSummary({
    film,
    rating: {},
    previous: { ...previous, mediaType: "tv" },
    entries,
    updatedAt
  });
  return compactObject({
    ...base,
    filmId: getWatchFilmId(film) || String(previous.filmId || ""),
    seasonKey:
      getWatchSeasonKey(season) ||
      getWatchSeasonKey(seasonRating) ||
      String(previous.seasonKey || ""),
    seasonNumber:
      getWatchSeasonNumber(season) ??
      getWatchSeasonNumber(seasonRating) ??
      previous.seasonNumber ?? null,
    seasonName:
      season.name || seasonRating.name || previous.seasonName || "Сезон",
    mediaType: "tv" as const
  });
};

export const buildVirtualSeasonWatchSummary = (seasonRating: SeasonRating = {}, film: FilmMetadata = {}, season: WatchSeason = {}) => {
  const firstEntry = createVirtualSeasonFirstViewing(seasonRating, film, season);
  if (!firstEntry) return null;
  return {
    ...buildSeasonWatchSummary({ film, season, seasonRating, entries: [firstEntry] }),
    virtual: true
  };
};

export const mergeRatingsWithWatchSummaries = (ratings: Rating[] = [], persistedSummaries: WatchSummary[] = []) => {
  const summariesById = new Map<string, WatchSummary>();
  persistedSummaries.forEach((summary) => {
    const filmId = getWatchFilmId(summary);
    if (filmId) summariesById.set(filmId, { ...summary, filmId, virtual: false });
  });
  ratings.forEach((rating: Rating) => {
    const filmId = getWatchFilmId(rating);
    if (!filmId || summariesById.has(filmId)) return;
    const summary = buildVirtualWatchSummary(rating, rating);
    if (summary) summariesById.set(filmId, summary);
  });
  return [...summariesById.values()];
};

export const calculateWatchStatistics = (summaries: WatchSummary[] = [], { start = null, end = null }: { start?: DateValue; end?: DateValue } = {}) => {
  const active = summaries.filter((summary) => Number(summary?.totalCount) > 0);
  const uniqueMovies = active.filter((summary) => getWatchMediaType(summary) === "movie").length;
  const uniqueSeries = active.filter((summary) => getWatchMediaType(summary) === "tv").length;
  const totalViews = active.reduce((sum, summary) => sum + Number(summary.totalCount || 0), 0);
  const rewatches = active.reduce(
    (sum, summary) => sum + Math.max(0, Number(summary.rewatchCount ?? Number(summary.totalCount) - 1) || 0),
    0
  );
  const periodStart = toWatchDate(start);
  const periodEnd = toWatchDate(end);
  const periodRewatches = active.reduce((sum, summary) => {
    const lastWatchedAt = toWatchDate(summary.lastWatchedAt);
    if (!lastWatchedAt || !periodStart || !periodEnd) return sum;
    return lastWatchedAt >= periodStart && lastWatchedAt < periodEnd && Number(summary.totalCount) > 1
      ? sum + 1
      : sum;
  }, 0);

  return {
    totalViews,
    uniqueWorks: active.length,
    uniqueMovies,
    uniqueSeries,
    rewatches,
    rewatchedWorks: active.filter((summary) => Number(summary.totalCount) > 1).length,
    rewatchShare: totalViews > 0 ? rewatches / totalViews : 0,
    periodRewatches,
    topRewatched: [...active]
      .filter((summary) => Number(summary.totalCount) > 1)
      .sort((first, second) =>
        Number(second.totalCount) - Number(first.totalCount) ||
        dateMillis(second.lastWatchedAt) - dateMillis(first.lastWatchedAt)
      )
  };
};

export const getRatingDate = (rating: Rating = {}) => {
  for (const value of [
    rating.firstRatedAt,
    rating.ratedAt,
    rating.firstTrackedAt,
    rating.updatedAt
  ]) {
    const date = toWatchDate(value);
    if (date) return date;
  }
  return null;
};

// The "All ratings" card describes the current saved rating, so its date is
// the editable watched date. Keep this separate from getRatingDate: charts and
// progress mechanics intentionally resolve the immutable first-rating date.
export const getCurrentRatingDate = (rating: Rating = {}) => {
  for (const value of [
    rating.ratedAt,
    rating.firstRatedAt,
    rating.firstTrackedAt,
    rating.updatedAt
  ]) {
    const date = toWatchDate(value);
    if (date) return date;
  }
  return null;
};

const ratingDateMillis = (rating: Rating = {}) => getRatingDate(rating)?.getTime() || 0;

export const filterAndSortWatchRatings = (
  ratings: Rating[] = [],
  summaries: WatchSummary[] | Map<string, WatchSummary> = [],
  { filter = "all", sort = "rating_date", now = new Date() }: { filter?: WatchFilter; sort?: WatchSort; now?: DateValue } = {}
) => {
  const summariesByFilmId = summaries instanceof Map
    ? summaries
    : new Map((summaries || []).map((summary) => [getWatchFilmId(summary), summary]));
  const recentBoundary = (toWatchDate(now)?.getTime() || Date.now()) - 30 * 24 * 60 * 60 * 1000;
  const withSummary = (ratings || []).map((rating: Rating) => ({
    rating,
    summary: summariesByFilmId.get(getWatchFilmId(rating)) || buildVirtualWatchSummary(rating, rating)
  }));
  const filtered = withSummary.filter(({ summary }) => {
    const total = Number(summary?.totalCount) || 0;
    if (filter === "once") return total === 1;
    if (filter === "rewatched") return total > 1;
    if (filter === "three_plus") return total >= 3;
    if (filter === "recent_rewatch") {
      return total > 1 && dateMillis(summary?.lastWatchedAt) >= recentBoundary;
    }
    return true;
  });

  filtered.sort((first, second) => {
    if (sort === "view_count") {
      return Number(second.summary?.totalCount || 0) - Number(first.summary?.totalCount || 0) ||
        dateMillis(second.summary?.lastWatchedAt) - dateMillis(first.summary?.lastWatchedAt);
    }
    if (sort === "last_view") {
      return dateMillis(second.summary?.lastWatchedAt) - dateMillis(first.summary?.lastWatchedAt) ||
        Number(second.summary?.totalCount || 0) - Number(first.summary?.totalCount || 0);
    }
    if (sort === "first_view") {
      return dateMillis(second.summary?.firstKnownAt) - dateMillis(first.summary?.firstKnownAt) ||
        ratingDateMillis(second.rating) - ratingDateMillis(first.rating);
    }
    return ratingDateMillis(second.rating) - ratingDateMillis(first.rating);
  });

  return filtered.map(({ rating }) => rating);
};
